import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, Edit3, Lock, Camera, DollarSign, CheckCircle, XCircle, Clock, Upload } from 'lucide-react';
import Navbar from '../components/Navbar';
import Avatar from '../components/Avatar';
import { useAuth } from '../context/AuthContext';
import API from '../api/axios';
import toast from 'react-hot-toast';

const ProfilePage = () => {
  const { user, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', mobile: '' });
  const [avatar, setAvatar] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [passForm, setPassForm] = useState({ currentPassword: '', newPassword: '' ,confirmPassword: ''});
  const [loading, setLoading] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [paymentModalData, setPaymentModalData] = useState(null);

  useEffect(() => {
    if (user) setForm({ name: user.name, mobile: user.mobile });
  }, [user]);

  useEffect(() => {
    if (activeTab === 'expenses') loadExpenses();
  }, [activeTab, user]);

  const loadExpenses = async () => {
    setExpensesLoading(true);
    try {
      const { data: meData } = await API.get('/auth/me');
      const userGroups = meData.user?.groups || [];
      if (!userGroups.length) {
        setExpenses([]);
        setExpensesLoading(false);
        return;
      }
      const results = await Promise.allSettled(
        userGroups.map(g => {
          const id = typeof g === 'string' ? g : g._id;
          return API.get(`/payments/my-expenses/${id}`);
        })
      );
      const all = results
        .filter(r => r.status === 'fulfilled')
        .flatMap(r => r.value.data.payments || []);
      setExpenses(all);
    } catch (err) {
      console.error('loadExpenses error:', err);
    } finally {
      setExpensesLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append('name', form.name);
    fd.append('mobile', form.mobile);
    if (avatar) fd.append('avatar', avatar);
    setLoading(true);
    try {
      await API.put('/users/profile', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('تم تحديث البروفايل ✅');
      await refreshUser();
      setEditing(false);
    } catch (err) { toast.error(err.response?.data?.message || 'فشل التحديث'); }
    finally { setLoading(false); }
  };

  const handleChangePassword = async (e) => {
  e.preventDefault();
  
  // Check if all fields are filled
  if (!passForm.currentPassword || !passForm.newPassword || !passForm.confirmPassword) {
    toast.error('يرجى ملء جميع الحقول');
    return;
  }
  
  // Check if passwords match
  if (passForm.newPassword !== passForm.confirmPassword) {
    toast.error('كلمة المرور الجديدة غير متطابقة');
    return;
  }
  
  // Check password length
  if (passForm.newPassword.length < 6) {
    toast.error('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
    return;
  }
  
  setLoading(true);
  try {
    await API.put('/users/change-password', {
      currentPassword: passForm.currentPassword,
      newPassword: passForm.newPassword
    });
    toast.success('تم تغيير كلمة المرور ✅');
    setPassForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
  } catch (err) {
    toast.error(err.response?.data?.message || 'فشل التغيير');
  } finally {
    setLoading(false);
  }
};

  const pendingExpenses = expenses.filter(e => !e.confirmedByAdmin);
  const confirmedExpenses = expenses.filter(e => e.confirmedByAdmin);
  const totalPending = pendingExpenses.reduce((s, e) => s + (e.amount || 0), 0);
  const totalConfirmed = confirmedExpenses.reduce((s, e) => s + (e.amount || 0), 0);

  return (
    <div className="page-wrapper">
      <Navbar />
      <div className="container" style={{ padding: '32px 20px', maxWidth: '700px' }}>

        {/* Profile Header */}
        <div className="card" style={{ marginBottom: '24px', padding: '28px', display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative' }}>
            <Avatar src={avatarPreview || user?.avatar} name={user?.name} size="xl" />
            {editing && (
              <label style={{ position: 'absolute', bottom: 0, left: 0, cursor: 'pointer' }}>
                <input type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={e => { const f = e.target.files[0]; setAvatar(f); setAvatarPreview(URL.createObjectURL(f)); }} />
                <div style={{ width: '28px', height: '28px', background: 'var(--primary)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Camera size={14} color="white" />
                </div>
              </label>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: '1.4rem', fontWeight: 800 }}>{user?.name}</h1>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>{user?.email}</p>
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <span className="badge badge-primary">{user?.groups?.length || 0} مجموعة</span>
              <span className="badge badge-secondary">{user?.role === 'admin' ? 'أدمن' : 'عضو'}</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: '24px', gap: '4px' }}>
          {[
            { id: 'profile', label: 'بياناتي', icon: <User size={15} /> },
            { id: 'expenses', label: 'مصاريفي 💳', icon: <DollarSign size={15} /> },
            { id: 'security', label: 'الأمان', icon: <Lock size={15} /> },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '12px 16px', background: 'none', border: 'none',
                borderBottom: activeTab === t.id ? '2px solid var(--primary)' : '2px solid transparent',
                color: activeTab === t.id ? 'var(--primary-light)' : 'var(--text-secondary)',
                fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', transition: 'var(--transition)'
              }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontWeight: 700 }}>المعلومات الشخصية</h2>
              {!editing && (
                <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>
                  <Edit3 size={14} /> تعديل
                </button>
              )}
            </div>

            {editing ? (
              <form onSubmit={handleUpdateProfile}>
                <div className="form-group">
                  <label className="form-label"><User size={14} /> الاسم</label>
                  <input type="text" className="form-input" value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label"><Phone size={14} /> رقم الموبايل</label>
                  <input type="tel" className="form-input" value={form.mobile} dir="ltr"
                    onChange={e => setForm({ ...form, mobile: e.target.value })} />
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                  <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setEditing(false)}>إلغاء</button>
                  <button type="submit" className="btn btn-primary" style={{ flex: 2 }} disabled={loading}>
                    {loading ? <div className="spinner spinner-sm" /> : null}
                    {loading ? 'جاري الحفظ...' : 'حفظ التغييرات'}
                  </button>
                </div>
              </form>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <InfoRow icon={<User size={16} />} label="الاسم" value={user?.name} />
                <InfoRow icon={<Mail size={16} />} label="البريد الإلكتروني" value={user?.email} />
                <InfoRow icon={<Phone size={16} />} label="الموبايل" value={user?.mobile} />
              </div>
            )}
          </div>
        )}

        {/* Expenses Tab */}
        {activeTab === 'expenses' && (
          <div>
            {/* Summary Cards */}
            <div className="grid-2" style={{ marginBottom: '20px' }}>
              <div className="card" style={{ textAlign: 'center', borderTop: '3px solid var(--warning)' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--warning)' }}>{totalPending} ج.م</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>في انتظار التأكيد</div>
              </div>
              <div className="card" style={{ textAlign: 'center', borderTop: '3px solid var(--success)' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--success)' }}>{totalConfirmed} ج.م</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>مؤكد الدفع</div>
              </div>
            </div>

            {expensesLoading ? (
              <div className="loading-screen"><div className="spinner" /></div>
            ) : expenses.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">💳</div>
                <h3>مفيش مصاريف لحد دلوقتي</h3>
                <p>بعد ما تختار في استطلاع مالي هتظهر هنا</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {expenses.map(expense => (
                  <ExpenseRow key={expense._id} expense={expense} onUpload={() => setPaymentModalData(expense)} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="card">
            <h2 style={{ fontWeight: 700, marginBottom: '20px' }}>تغيير كلمة المرور</h2>
            <form onSubmit={handleChangePassword}>
              <div className="form-group">
                <label className="form-label"><Lock size={14} /> كلمة المرور الحالية</label>
                <input type="password" className="form-input" value={passForm.currentPassword} dir="ltr"
                  onChange={e => setPassForm({ ...passForm, currentPassword: e.target.value })} placeholder="••••••••" />
              </div>
              <div className="form-group">
                <label className="form-label"><Lock size={14} /> كلمة المرور الجديدة</label>
                <input type="password" className="form-input" value={passForm.newPassword} dir="ltr"
                  onChange={e => setPassForm({ ...passForm, newPassword: e.target.value })} placeholder="6 أحرف على الأقل" />
              </div>
              <div className="form-group">
                <label className="form-label"><Lock size={14} /> تأكيد كلمة المرور الجديدة</label>
                <input type="password" className="form-input" value={passForm.confirmPassword} dir="ltr"
                  onChange={e => setPassForm({ ...passForm, confirmPassword: e.target.value })} placeholder="أعد إدخال كلمة المرور" />
              </div>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? <div className="spinner spinner-sm" /> : <Lock size={16} />}
                {loading ? 'جاري الحفظ...' : 'تغيير كلمة المرور'}
              </button>
            </form>
          </div>
        )}
      </div>

      {paymentModalData && (
        <UploadReceiptModal payment={paymentModalData} onClose={() => { setPaymentModalData(null); loadExpenses(); }} />
      )}
    </div>
  );
};

const InfoRow = ({ icon, label, value }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)' }}>
    <div style={{ color: 'var(--primary-light)' }}>{icon}</div>
    <div>
      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{label}</div>
      <div style={{ fontWeight: 600 }}>{value || '-'}</div>
    </div>
  </div>
);

const ExpenseRow = ({ expense, onUpload }) => (
  <div className="card" style={{ padding: '16px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
      <div>
        <div style={{ fontWeight: 700 }}>{expense.survey?.title}</div>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '2px' }}>
          الاختيار: <strong style={{ color: 'var(--text-primary)' }}>{expense.optionName}</strong>
        </div>
      </div>
      <span className="badge badge-warning">{expense.amount} ج.م</span>
    </div>

    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      {expense.confirmedByAdmin ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--success)', fontSize: '0.82rem', fontWeight: 600 }}>
          <CheckCircle size={14} /> تم تأكيد الدفع
        </span>
      ) : expense.receiptUploaded ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--warning)', fontSize: '0.82rem', fontWeight: 600 }}>
          <Clock size={14} /> في انتظار تأكيد الأدمن
        </span>
      ) : (
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--danger)', fontSize: '0.82rem', fontWeight: 600 }}>
          <XCircle size={14} /> لم يتم الدفع
        </span>
      )}

      {!expense.confirmedByAdmin && (
        <button className="btn btn-primary btn-sm" onClick={onUpload}>
          <Upload size={13} /> {expense.receiptUploaded ? 'إعادة رفع الإيصال' : 'رفع إيصال الدفع'}
        </button>
      )}
    </div>
  </div>
);

const UploadReceiptModal = ({ payment, onClose }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleUpload = async () => {
    if (!file) { toast.error('يرجى اختيار صورة الإيصال'); return; }
    const fd = new FormData();
    fd.append('receipt', file);
    setLoading(true);
    try {
      await API.post(`/payments/${payment._id}/upload-receipt`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('تم رفع الإيصال بنجاح ✅');
      onClose();
    } catch (err) { toast.error(err.response?.data?.message || 'فشل الرفع'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">رفع إيصال الدفع</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div style={{ marginBottom: '16px', padding: '14px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)' }}>
          <div style={{ fontWeight: 600 }}>{payment.survey?.title}</div>
          <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
            {payment.optionName} — <strong style={{ color: 'var(--warning)' }}>{payment.amount} ج.م</strong>
          </div>
        </div>

        <label className="file-upload-area" style={{ display: 'block', cursor: 'pointer', marginBottom: '16px' }}>
          <input type="file" accept="image/*" style={{ display: 'none' }}
            onChange={e => { const f = e.target.files[0]; setFile(f); setPreview(URL.createObjectURL(f)); }} />
          {preview ? (
            <img src={preview} alt="" style={{ width: '100%', maxHeight: '250px', objectFit: 'contain', borderRadius: 'var(--radius-md)' }} />
          ) : (
            <div style={{ padding: '30px', textAlign: 'center', color: 'var(--text-muted)' }}>
              <Upload size={28} style={{ marginBottom: '8px', opacity: 0.5 }} />
              <p style={{ fontWeight: 600 }}>اضغط لاختيار صورة الإيصال</p>
              <p style={{ fontSize: '0.8rem', marginTop: '4px' }}>JPG, PNG (max 5MB)</p>
            </div>
          )}
        </label>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-secondary" style={{ flex: 1 }} onClick={onClose}>إلغاء</button>
          <button className="btn btn-primary" style={{ flex: 2 }} onClick={handleUpload} disabled={!file || loading}>
            {loading ? <div className="spinner spinner-sm" /> : <Upload size={16} />}
            {loading ? 'جاري الرفع...' : 'رفع وتأكيد'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;